import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Capability } from '../../components/nav';

export type UserRole = 'admin' | 'manager' | 'user' | 'coordinator';

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin / Majitel',
  manager: 'Vedoucí výroby',
  user: 'Dílna / Řemeslník',
  coordinator: 'Koordinátor nabídek',
};

export interface AuthUser {
  id: string;
  fullName: string;
  email: string;
  role: UserRole;
  caps: Capability[];
  defaultRoleLabel: string | null;
  hourlyRate: string | null;
  rateCurrency: string;
  active: boolean;
  mustChangePassword: boolean;
}

export const useCan = (user: AuthUser | undefined, cap: Capability): boolean => !!user?.caps.includes(cap);

export const ME_KEY = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => api.get<{ user: AuthUser }>('/api/auth/me').then((r) => r.user),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; password: string }) =>
      api.post<{ user: AuthUser }>('/api/auth/login', vars).then((r) => r.user),
    onSuccess: (user) => qc.setQueryData(ME_KEY, user),
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { currentPassword: string; newPassword: string }) =>
      api.post<{ user: AuthUser }>('/api/auth/change-password', vars).then((r) => r.user),
    onSuccess: (user) => qc.setQueryData(ME_KEY, user),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout', {}),
    // Po odhlášení nesmí na sdíleném zařízení nic zůstat: ani cache odpovědí API pro offline,
    // ani nic v paměti stránky. Proto úplné načtení, to skončí na přihlášení.
    onSuccess: async () => {
      qc.clear();
      const keys = (await window.caches?.keys()) ?? [];
      await Promise.all(keys.map((k) => window.caches.delete(k)));
      window.location.replace('/');
    },
  });
}
